const INPUT_MARKER = '/input/';

function sanitizeTenantSeg(raw) {
  const s = String(raw ?? '')
    .replace(/[^a-zA-Z0-9._@-]/g, '_')
    .slice(0, 200);
  return s || 'shared';
}

/** Match backend normalize_pool_agent_id: UI folder "Network" is the Shared pool. */
export function normalizePoolAgentId(agentId) {
  if (agentId == null || String(agentId).trim() === '') return 'Shared';
  const s = String(agentId).trim();
  if (s.toLowerCase() === 'network') return 'Shared';
  return s;
}

const COLD_DEFAULT = { tenantId: 'shared', customerId: '_tenant' };

/**
 * Cold sync / unsync: tenant query params must match S3 layout (see lancedb_paths.memory_tenant_from_input_key).
 * Canonical: Shared/, Tenants/{id}/, Agents/{agent}/Shared|Tenants/…
 */
export function parseMemoryColdContext(s3Key) {
  if (!s3Key || typeof s3Key !== 'string') {
    return { ...COLD_DEFAULT };
  }
  const pos = s3Key.indexOf(INPUT_MARKER);
  if (pos === -1) {
    return { ...COLD_DEFAULT };
  }
  const parts = s3Key
    .slice(pos + INPUT_MARKER.length)
    .split('/')
    .filter(Boolean);
  if (parts.length < 2) {
    return { ...COLD_DEFAULT };
  }

  const pl0 = parts[0].toLowerCase();

  if (pl0 === 'agents') {
    if (parts.length < 3) {
      return { ...COLD_DEFAULT };
    }
    if (parts.length === 3) {
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    const s2 = parts[2].toLowerCase();
    if (s2 === 'shared' || parts[2] === '_unspecified') {
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    if (s2 === 'tenants') {
      if (parts.length >= 5) {
        return { tenantId: sanitizeTenantSeg(parts[3]), customerId: '_tenant' };
      }
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    return { tenantId: sanitizeTenantSeg(parts[2]), customerId: '_tenant' };
  }

  if (pl0 === 'tenants' && parts.length >= 3) {
    return { tenantId: sanitizeTenantSeg(parts[1]), customerId: '_tenant' };
  }

  if (pl0 === 'shared') {
    if (parts.length === 2) {
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    const seg2 = parts[1];
    const sl = seg2.toLowerCase();
    if (sl === 'shared' || seg2 === '_unspecified') {
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    if (sl === 'tenants' && parts.length >= 4) {
      return { tenantId: sanitizeTenantSeg(parts[2]), customerId: '_tenant' };
    }
    if (sl === 'tenants') {
      return { ...COLD_DEFAULT };
    }
    return { tenantId: sanitizeTenantSeg(seg2), customerId: '_tenant' };
  }

  if (pl0 === 'network') {
    if (parts.length < 3) {
      return { ...COLD_DEFAULT };
    }
    const seg2 = parts[1];
    const sl = seg2.toLowerCase();
    if (sl === 'shared' || seg2 === '_unspecified') {
      return { tenantId: 'shared', customerId: '_tenant' };
    }
    if (sl === 'tenants' && parts.length >= 4) {
      return { tenantId: sanitizeTenantSeg(parts[2]), customerId: '_tenant' };
    }
    return { tenantId: sanitizeTenantSeg(seg2), customerId: '_tenant' };
  }

  if (parts.length < 3) {
    return { ...COLD_DEFAULT };
  }
  const seg2 = parts[1];
  const sl = seg2.toLowerCase();
  if (sl === 'shared' || seg2 === '_unspecified') {
    return { tenantId: 'shared', customerId: '_tenant' };
  }
  if (sl === 'tenants' && parts.length >= 4) {
    return { tenantId: sanitizeTenantSeg(parts[2]), customerId: '_tenant' };
  }
  return { tenantId: sanitizeTenantSeg(seg2), customerId: '_tenant' };
}

function documentIdFromParts(parts, startIdx) {
  const rel = parts.slice(startIdx).join('/');
  const documentId = rel.split('/').pop();
  if (!documentId || documentId.endsWith('/')) {
    return null;
  }
  return documentId;
}

/**
 * Parse S3 key for memory files under .../input/.
 * Canonical: Shared/…, Tenants/…, Agents/{agent}/…; legacy Network/… and agent-at-root.
 */
export function parseMemoryFileContext(s3Key) {
  if (!s3Key || typeof s3Key !== 'string') return null;
  const legacy = /\/input\/([^/]+)\/(core_instructions|knowledge_base)\/([^/]+)$/;
  const mLegacy = s3Key.match(legacy);
  if (mLegacy) {
    const agentId = normalizePoolAgentId(mLegacy[1]);
    const folder = mLegacy[2];
    const documentId = mLegacy[3];
    if (!documentId || documentId.endsWith('/')) return null;
    return { agentId, folder, documentId, s3Key };
  }
  const pos = s3Key.indexOf(INPUT_MARKER);
  if (pos === -1) return null;
  const parts = s3Key.slice(pos + INPUT_MARKER.length).split('/').filter(Boolean);
  if (parts.length < 2) return null;

  const pl0 = parts[0].toLowerCase();

  if (pl0 === 'agents' && parts.length >= 3) {
    const agentId = normalizePoolAgentId(parts[1]);
    const documentId = documentIdFromParts(parts, 2);
    if (!documentId) return null;
    return { agentId, folder: 'nested', documentId, s3Key };
  }

  if (pl0 === 'tenants' && parts.length >= 3) {
    const documentId = documentIdFromParts(parts, 1);
    if (!documentId) return null;
    return { agentId: 'Shared', folder: 'nested', documentId, s3Key };
  }

  if (pl0 === 'shared') {
    const documentId = documentIdFromParts(parts, 1);
    if (!documentId) return null;
    return { agentId: 'Shared', folder: 'nested', documentId, s3Key };
  }

  if (pl0 === 'network' && parts.length >= 2) {
    const documentId = documentIdFromParts(parts, 1);
    if (!documentId) return null;
    return { agentId: 'Shared', folder: 'nested', documentId, s3Key };
  }

  const agentId = normalizePoolAgentId(parts[0]);
  const documentId = documentIdFromParts(parts, 1);
  if (!documentId) return null;
  return { agentId, folder: 'nested', documentId, s3Key };
}

/** Layer-specific completion delays (no WebSocket COMPLETE from SFN today) */
export const MEMORY_SYNC_MS = { hot: 4000, cold: 45000 };
export const MEMORY_UNSYNC_MS = 3000;

/**
 * Shape from GET /networks/:id → network.agents[logicalId].{core_instructions_metadata, vector_knowledge_metadata}
 */
export function buildMemorySeed(agents) {
  const hot = {};
  const cold = {};
  if (!agents || typeof agents !== 'object') return { hot, cold };
  for (const [agentId, cfg] of Object.entries(agents)) {
    if (cfg && Array.isArray(cfg.core_instructions_metadata)) {
      hot[agentId] = cfg.core_instructions_metadata;
    }
    if (cfg && Array.isArray(cfg.vector_knowledge_metadata)) {
      cold[agentId] = cfg.vector_knowledge_metadata;
    }
  }
  return { hot, cold };
}

/** @typedef {'off' | 'hot' | 'cold'} MemorySegmentMode */

/**
 * Source of truth from GET /networks/:id — mutually exclusive (hot list checked first).
 */
export function deriveSeedMemoryMode(memorySeed, agentId, documentId) {
  const hot = memorySeed?.hot?.[agentId];
  const cold = memorySeed?.cold?.[agentId];
  if (Array.isArray(hot) && hot.includes(documentId)) return 'hot';
  if (Array.isArray(cold) && cold.includes(documentId)) return 'cold';
  return 'off';
}

/**
 * Per-row segment + loading for MemorySegmentedControl.
 * Committed session mode overrides seed; while syncing, UI shows target segment + spinner.
 */
export function deriveMemorySegment(mem, memorySeed, agentId, documentId) {
  if (mem?.syncing && mem.pendingTarget != null) {
    return { segment: mem.pendingTarget, loading: true };
  }
  if (mem?.mode != null) {
    return { segment: mem.mode, loading: false };
  }
  return { segment: deriveSeedMemoryMode(memorySeed, agentId, documentId), loading: false };
}
